import { useEffect, useState } from "react";
import { Button, Input, Modal, Select, useToast } from "@astrum/ui";
import { formatClock, parseClock } from "@astrum/shared";
import type { TimeEntry } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import type { Task } from "../lib/api.js";
import "./log-time-modal.css";

/**
 * Add a time entry by hand, or edit one. From a task the task is fixed; from
 * the timesheet the person picks one of the tasks they are assigned to.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (entry: TimeEntry) => void;
  /** Today in the person's own calendar; the latest day that may be logged. */
  today: string;
  /** Pre-filled day for a new entry. */
  defaultDate: string;
  /** Fixes the task (the task detail page). Omit to show a task picker. */
  task?: { id: string; title: string };
  /** Edit this entry instead of creating one. */
  entry?: TimeEntry;
}

export function LogTimeModal({ open, onClose, onSaved, today, defaultDate, task, entry }: Props) {
  const { toast } = useToast();
  const [date, setDate] = useState(defaultDate);
  const [duration, setDuration] = useState("");
  const [note, setNote] = useState("");
  const [taskId, setTaskId] = useState(task?.id ?? "");
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<Pick<Task, "id" | "title" | "projectCode">[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Start from the entry being edited, or a clean form, each time it opens.
  useEffect(() => {
    if (!open) return;
    setDate(entry?.date ?? defaultDate);
    setDuration(entry ? formatClock(entry.minutes) : "");
    setNote(entry?.note ?? "");
    setTaskId(task?.id ?? entry?.taskId ?? "");
    setSearch("");
    setError(null);
  }, [open, entry, defaultDate, task?.id]);

  const picking = !task && !entry;
  useEffect(() => {
    if (!open || !picking) return;
    const params = new URLSearchParams({ assignee: "me", includeSubtasks: "true", limit: "50" });
    if (search.trim()) params.set("search", search.trim());
    const timer = setTimeout(() => {
      api
        .get<{ tasks: Task[] }>(`/tasks?${params.toString()}`)
        .then((res) => setOptions(res.tasks))
        .catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [open, picking, search]);

  const submit = async () => {
    const minutes = parseClock(duration);
    if (minutes === null || minutes <= 0) {
      setError("Enter a time like 1:30, 90m or 1.5h (up to 24 hours).");
      return;
    }
    if (!taskId) {
      setError("Choose a task.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const res = entry
        ? await api.patch<{ entry: TimeEntry }>(`/time/entries/${entry.id}`, { date, minutes, note: note.trim() || null })
        : await api.post<{ entry: TimeEntry }>("/time/entries", { taskId, date, minutes, ...(note.trim() ? { note: note.trim() } : {}) });
      onSaved(res.entry);
      onClose();
    } catch (e) {
      const message = e instanceof ApiError ? e.message : "Could not save that time.";
      setError(message);
      toast(message, { tone: "danger" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={entry ? "Edit time" : "Log time"}>
      <form
        className="log-time"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {task ? (
          <p className="log-time__task">{task.title}</p>
        ) : entry ? (
          <p className="log-time__task">{entry.taskTitle}</p>
        ) : (
          <>
            <Input label="Find a task" placeholder="Search your tasks" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select label="Task" value={taskId} onChange={(e) => setTaskId(e.target.value)}>
              <option value="">Choose a task…</option>
              {options.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.projectCode ? `${t.projectCode} · ` : ""}
                  {t.title}
                </option>
              ))}
            </Select>
          </>
        )}
        <div className="log-time__row">
          <Input label="Day" type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} required />
          <Input
            label="Time"
            placeholder="1:30"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            hint="Hours and minutes: 1:30, 90m, 1.5h"
            autoFocus
          />
        </div>
        <Input label="Note (optional)" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        {error && (
          <p className="log-time__error" role="alert">
            {error}
          </p>
        )}
        <div className="log-time__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            {entry ? "Save" : "Log time"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
